import { Checkbox } from '@acceso/design-system';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TClip } from '@/features/clips/types';
import { TProgram } from '@/features/programs/types';
import { ClipSyncStatusEnum } from '@/features/segments/types';
import { useSelectActiveItem } from '@/hooks/useSelectActiveItem';
import { useUnderMinDesktopWidth } from '@/hooks/useUnderMinDesktopWidth';
import { useClipPresence } from '@/providers/websocket';
import { useSelectedItemsStore } from '@/stores/selected-items';

import { ClipMiniCardBaseCardBody } from './components/ClipMiniCardBaseCardBody';
import { ClipMiniCardBaseCardHeader } from './components/ClipMiniCardBaseCardHeader';

export type ClipMiniCardBaseProps = {
  item: TClip;
  program?: TProgram;
  isActive?: boolean;
};
export function ClipMiniCard({ item, program, isActive }: ClipMiniCardBaseProps) {
  const { activeClip, compactUis, setActiveClip, setVisibleBox } = useBroadcastScheduleProgram();
  const store = useSelectedItemsStore();
  const { underMinDesktopWith } = useUnderMinDesktopWidth();
  const userConnectedInClips = useClipPresence();

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isActive && ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [isActive]);

  const selectClip = useSelectActiveItem({
    activeItem: activeClip,
    setActiveItem: setActiveClip,
  });

  const handleClickItem = useCallback(
    event => {
      event.stopPropagation();
      setVisibleBox(null);
      selectClip(item);
    },
    [item, selectClip, setVisibleBox]
  );

  const isDetailActive = useMemo(() => {
    if (!activeClip) return false;
    return activeClip.id === `${item.id}`;
  }, [activeClip, item.id]);

  const handleCheckboxChange = (id: string) => {
    // While "select all" is on, every card is drawn checked from the flag alone,
    // so unchecking one has to turn the flag off too: otherwise the card stays
    // checked on screen and the selection sync puts the clip back in.
    if (store.toggleAllActived) {
      store.dispatch({ type: 'removeToggleAll' });
    }
    store.dispatch({
      type: 'toggleId',
      payload: id,
    });
  };

  const syncStatusMapper: Record<ClipSyncStatusEnum, string> = {
    [ClipSyncStatusEnum.FAILED]: 'bg-feedback-error-20/10',
    [ClipSyncStatusEnum.COMPLETED]: 'bg-success-light/30',
    [ClipSyncStatusEnum.WAITING_ENRICHMENTS]: 'bg-extra-80/10',
  };

  const containerClassName = syncStatusMapper[item.sync_status];
  const isOccupiedByReviewer = !!userConnectedInClips[item.id]?.length;

  return (
    <article
      className={clsx(
        'group/broadcasterCard flex w-full min-w-[256px] cursor-pointer items-start justify-start border-b-[1px] border-neutral-30 pl-2 pt-2 transition-opacity duration-300 hover:bg-black/[.05]',
        !compactUis && 'flex-col',
        isDetailActive &&
          'bg-neutral-15 shadow-[inset_8px_0_0_0_var(--tw-shadow-color)] shadow-accent-active-primary-30',
        store.include.includes(item?.id) &&
          'shadow-[inset_8px_0_0_0_var(--tw-shadow-color)] shadow-accent-active-primary-40',
        containerClassName,
        isOccupiedByReviewer && 'reviewer-occupied'
      )}
      onClick={event => handleClickItem(event)}
    >
      <div
        className={clsx(
          'flex w-full items-start gap-2 pr-5',
          underMinDesktopWith && 'flex-col pl-1.5'
        )}
        ref={ref}
      >
        <Checkbox
          onChange={() => {
            handleCheckboxChange(item.id);
          }}
          labelClassName="ml-1"
          checked={store.include.includes(item.id) || store.toggleAllActived}
        />
        <div className={clsx('flex w-full gap-2', !compactUis && 'flex-col')}>
          <ClipMiniCardBaseCardHeader item={item} program={program} />
          <ClipMiniCardBaseCardBody item={item} />
        </div>
      </div>
    </article>
  );
}

export const ClipMiniCardBase = React.memo(ClipMiniCard);
